/**
 * This file gathers some of the differences between using Vue 2.6, Vue 2.7 and
 * Vue 3 in a common compatibility layer. This way, we need to modify fewer
 * source code files when upgrading to Vue 2.7 or Vue 3.
 */

import { invariant } from '@mimir/common/util/invariant';
import type { PropType as VuePropType, VueConstructor as VueCompatConstructor } from 'vue';
import Vue, { defineAsyncComponent as defineAsyncComponentV2, getCurrentInstance as vueGetCurrentInstance } from 'vue';
export {
  computed,
  defineComponent,
  nextTick,
  reactive,
  readonly,
  ref,
  watch,
  onMounted,
  onUnmounted,
  onBeforeUnmount,
  onUpdated,
  provide,
  inject,
} from 'vue';

export type { ComputedRef, Ref } from 'vue';

/*VUE3*/// export { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
/*VUE2*/ export { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router/composables';

export type PropType<T> = VuePropType<T>;
export type VueConstructor<V extends Vue = Vue> = VueCompatConstructor<V>;
import type { Breakpoint } from 'vuetify/types/services/breakpoint';
import type { Theme } from 'vuetify/types/services/theme';
import type { AsyncComponentLoader, AsyncComponentOptions, Component, ComponentPublicInstance } from './defineAsyncComponent';
/*VUE3*/// import { useDisplay as useVuetifyDisplay } from 'vuetify-next';
/*VUE2*/ const useVuetifyDisplay = () => ({} as Breakpoint);
/*VUE3*/// import { useTheme as useVuetifyTheme, type ThemeInstance } from 'vuetify';
/*VUE2*/ const useVuetifyTheme = () => ({} as any);
/*VUE3*/// type VuetifyThemeReturn = ThemeInstance;
/*VUE2*/ type VuetifyThemeReturn = Theme;




export const getCurrentInstance = vueGetCurrentInstance;

export function castRef<T extends VueCompatConstructor>(ref: any): InstanceType<T> {
  return ref as unknown as InstanceType<T>;
}

export function vueSet(object: any, propertyName: string | number, propertyValue: any) {
  Vue.set(object, propertyName, propertyValue);
}

export function vueDelete(object: any, propertyName: string) {
  Vue.delete(object, propertyName);
}

export function useRouter() {
  const vm = vueGetCurrentInstance();
  invariant(vm, 'useRouter() may only be used inside setup().');
  return vm.proxy.$router;
}

/**
 * This function replaces the content of the given existing object with
 * the content of the updated object. It is meant to be used when
 * we want to update an object without creating a new object reference,
 * for instance for updating an object that exists as a value in a map.
 */
export function vueUpdateObjectContent(existingObject: any, updatedObject: any) {
  const existingKeys = Object.keys(existingObject);
  const updatedKeys = Object.keys(updatedObject);

  // Let's remove keys that are no longer there
  for (const key of existingKeys) {
    if (!updatedKeys.includes(key)) {
      vueDelete(existingObject, key);
    }
  }

  // Let's set updated properties
  for (const key of updatedKeys) {
    vueSet(existingObject, key, updatedObject[key]);
  }
}

export function mdi(iconName: string) {
  if (import.meta.env.VUE_APP_V2) return 'mdi-' + iconName;
  else return 'mdi:mdi-' + iconName;
}

export function useDisplay() {
  return import.meta.env.VUE_APP_V2 ? vueGetCurrentInstance()!.proxy.$vuetify.breakpoint : useVuetifyDisplay();
}


export function useTheme(): VuetifyThemeReturn {
  return import.meta.env.VUE_APP_V2
    ? vueGetCurrentInstance()!.proxy.$vuetify.theme
    : useVuetifyTheme();
}

// This function needs to be used instead of directly using defineAsyncComponent from 'vue'
// because the type definitions differ between Vue 2.7 and Vue 3.
// By borrowing some types from Vue 3 we can allow for some type safety and compile time checks
// that are not natively available in Vue 2.7.
export function defineAsyncComponent<T extends Component = {
  new (): ComponentPublicInstance;
}>(source: AsyncComponentLoader<T> | AsyncComponentOptions<T>): T {
  const castedSource = source as Parameters<typeof defineAsyncComponentV2>[0];
  return defineAsyncComponentV2(castedSource) as unknown as T;
}

